import { useOutletContext } from 'react-router';
import { Card, MoneyCell, DateCell } from '@afrilink/ui';
import type { Service, BillingCycle } from '@/domains/hosting';
import { relativeExpiry } from '@/shared/utils/format';

const CYCLE_LABEL: Record<BillingCycle, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  'semi-annually': 'Semi-annually',
  annually: 'Annually',
  biennially: 'Biennially',
  triennially: 'Triennially',
};

export default function ServiceOverviewTab() {
  const service = useOutletContext<Service>();

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <SectionCard title="Plan">
          <Field label="Plan name"           value={service.plan.name} />
          <Field label="Description"         value={service.plan.description} />
          <Field label="Monthly equivalent"  value={<MoneyCell value={service.plan.monthlyPriceZAR} />} />
        </SectionCard>

        <SectionCard title="Billing">
          <Field label="Billing cycle"     value={CYCLE_LABEL[service.billingCycle]} />
          <Field label="Recurring charge"  value={<MoneyCell value={service.recurringAmountZAR} />} />
          <Field
            label="Next due"
            value={
              <span className="inline-flex items-center gap-1">
                <DateCell value={service.nextDueDate} format="long" /> ({relativeExpiry(service.nextDueDate)})
              </span>
            }
          />
          <Field label="Subscribed" value={<DateCell value={service.registeredAt} format="long" />} />
        </SectionCard>

        {service.notes && (
          <SectionCard title="Notes">
            <p className="text-sm text-foreground-soft">{service.notes}</p>
          </SectionCard>
        )}
      </div>

      <div className="space-y-6">
        <SectionCard title="Server">
          <Field label="Server"          value={service.serverHostname} mono />
          <Field label="cPanel username" value={service.cpanelUsername} mono />
          <Field label="Primary domain"  value={service.primaryDomain} mono />
        </SectionCard>
      </div>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card interactive={false}>
      <h3 className="border-b border-border px-6 py-3 text-sm font-semibold text-navy-primary">
        {title}
      </h3>
      <div className="space-y-3 px-6 py-5">{children}</div>
    </Card>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-3 gap-4 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`col-span-2 text-navy-primary ${mono ? 'text-xs tabular-nums' : ''}`}>
        {value}
      </dd>
    </div>
  );
}
